#!/usr/bin/env node
import path from "node:path";
import url from "node:url";
import fs from "node:fs/promises";
import { existsSync, mkdirSync, writeFileSync } from "node:fs";
import { analyzeCodebase } from "./analyzers/codebase";
import { analyzeFigma } from "./analyzers/figma";
import { generateLintBaseline } from "./analyzers/lintBaseline";
import { writeReport } from "./reporters/json";
import { findLatestReportJson, generateMarkdown } from "./reporters/markdown";
import { generateOverview } from "./reporters/overview";
import { exportMigrationCsv } from "./reporters/migrationCsv";
import { attachAbsRoot } from "./utils/walker";
// 0.8.10 — 공유 유틸 (JSON 파싱 방어 / 날짜 stamp / 최신 파일 탐색).
import { readJsonFile } from "./utils/readJson";
import { todayStampUtc } from "./utils/dateStamp";
import { findLatestName } from "./utils/latest";
import { renderDashboard } from "./dashboard";
import type {
  UIHealthConfig,
  CodebaseReport,
  FigmaInstancesFile,
  LighthouseConfig,
} from "./types";

// dotenv 즉시 로드 블록 제거 (이전: __dirname/../.env.local 강제).
// configPath 확정 후 main() 안에서 위치 결정 — 분리 후에도 동작하도록.

async function loadConfig(configPath: string): Promise<UIHealthConfig> {
  const abs = path.resolve(configPath);
  // v0.1.0: 사용자 환경 dsmonitor.config.ts (.ts) import 흐름 = tsx/esm/api 활용.
  // .js / .mjs 케이스는 native dynamic import 활용.
  // tsx 안 default export quirk — `{ default: { default: <config> } }` 형식 가능.
  const isTs = abs.endsWith(".ts") || abs.endsWith(".mts") || abs.endsWith(".cts");
  let mod: any;
  if (isTs) {
    const { tsImport } = await import("tsx/esm/api");
    mod = await tsImport(url.pathToFileURL(abs).href, import.meta.url);
  } else {
    mod = await import(url.pathToFileURL(abs).href);
  }
  // default export unwrap — tsx quirk 일치 (mod.default.default → mod.default → mod)
  const cfg = (mod?.default?.default ?? mod?.default ?? mod) as UIHealthConfig;
  return cfg;
}

/**
 * cwd 부터 위로 거슬러 올라가며 dsmonitor 설정 파일을 검색.
 *
 * 후보 (각 디렉토리에서 순서대로):
 *   1. <dir>/dsmonitor.config.ts          ← cd dsmonitor 안에서 직접 실행 케이스
 *   2. <dir>/dsmonitor/dsmonitor.config.ts ← 루트에서 호출 시 프로젝트 디렉토리 디렉토리 케이스
 *   3. <dir>/vitaui.config.ts             ← legacy (vitaui → dsmonitor rename, 0.2.0 부터 deprecation)
 *   4. <dir>/vitaui/vitaui.config.ts      ← legacy (vitaui → dsmonitor rename, 0.2.0 부터 deprecation)
 */
function findConfigUpward(startDir: string): string | null {
  let dir = path.resolve(startDir);
  while (true) {
    const candidates = [
      path.join(dir, "dsmonitor.config.ts"),
      path.join(dir, "dsmonitor", "dsmonitor.config.ts"),
      // legacy fallback — vitaui → dsmonitor rename (0.1.1 부터) 호환성 보존
      path.join(dir, "vitaui.config.ts"),
      path.join(dir, "vitaui", "vitaui.config.ts"),
    ];
    for (const candidate of candidates) {
      if (existsSync(candidate)) return candidate;
    }
    const parent = path.dirname(dir);
    if (parent === dir) return null;
    dir = parent;
  }
}

type AuditOnly = "code" | "figma" | "lighthouse" | undefined;

function parseArgs(argv: string[]): {
  cmd: string;
  baseline: boolean;
  configPath: string | null;
  only: AuditOnly;
  all: boolean;
  skipLighthouse: boolean;
  envPath?: string;
  inputPath?: string;
  outputPath?: string;
  doctorJson: boolean;
  doctorStrict: boolean;
} {
  const args = argv.slice(2);
  const cmd = args[0] || "audit";
  const baseline = args.includes("--baseline");
  const all = args.includes("--all");
  const skipLighthouse = args.includes("--skip-lighthouse");
  // 0.7.0 (BB): doctor 명령 옵션.
  const doctorJson = args.includes("--json");
  const doctorStrict = args.includes("--strict");
  const readOpt = (flag: string): string | undefined => {
    const i = args.indexOf(flag);
    return i >= 0 && args[i + 1] ? args[i + 1] : undefined;
  };
  const explicitConfig = readOpt("--config");
  const configPath = explicitConfig
    ? path.resolve(explicitConfig)
    : findConfigUpward(process.cwd());

  // --only <code|figma|lighthouse> 인자 검증
  const onlyRaw = readOpt("--only");
  let only: AuditOnly;
  if (onlyRaw === "code" || onlyRaw === "figma" || onlyRaw === "lighthouse") {
    only = onlyRaw;
  } else if (onlyRaw !== undefined) {
    console.error(
      `[dsmonitor] --only 인자는 "code" / "figma" / "lighthouse" 만 허용. 받은 값: "${onlyRaw}"`
    );
    process.exit(1);
  }

  // --only 와 --all 동시 사용 X — 의미 충돌 (only=부분 측정, all=통합 측정).
  if (only !== undefined && all) {
    console.error(
      `[dsmonitor] --only 와 --all 은 동시 사용 불가. ` +
        `--all 은 모든 측정 (code + figma + Lighthouse) + report + dashboard 통합 chain 흐름.`
    );
    process.exit(1);
  }

  return {
    cmd,
    baseline,
    configPath,
    only,
    all,
    skipLighthouse,
    envPath: readOpt("--env"),
    inputPath: readOpt("--input"),
    outputPath: readOpt("--output"),
    doctorJson,
    doctorStrict,
  };
}

// 0.8.9 — 명령 목록 단일 원천: `--help` 출력 + Unknown command 안내 공용.
//   옛 흐름: 목록이 Unknown command 안내에만 있었고 init / doctor 누락 + `--help`
//   미구현 (init 완료 안내가 `npx dsmonitor --help` 를 권하는데 exit 2 로 어긋남).
const COMMAND_LIST =
  `    init                                                    — 부트스트랩 (인터랙티브, config 없어도 실행 가능)\n` +
  `    audit [--only code|figma|lighthouse] [--baseline]       — 측정 (code + figma 통합 또는 부분별, v0.3.1: lighthouse 추가)\n` +
  `    audit --all [--baseline] [--skip-lighthouse]            — 통합 측정 chain (code + figma + Lighthouse + report + dashboard, v0.3.0)\n` +
  `    doctor [--json] [--strict]                              — config / 환경변수 진단\n` +
  `    baseline-lint                                           — ESLint 위반 baseline 생성\n` +
  `    report [--input <path>] [--output <path>]               — 측정 JSON → markdown 변환\n` +
  `    dashboard [--input <path>] [--output <path>]            — 측정 JSON → HTML 대시보드\n` +
  `    export-migration --frame=<comment> [--ds=<label>]       — frame 별 instance CSV (Phase 0.7)`;

async function main() {
  const {
    cmd,
    baseline,
    configPath,
    only,
    all,
    skipLighthouse,
    envPath,
    inputPath,
    outputPath,
    doctorJson,
    doctorStrict,
  } = parseArgs(process.argv);

  // 0.8.9 — help: config 탐색 전에 처리 (config 없어도 안내 가능해야 함).
  if (cmd === "--help" || cmd === "-h" || cmd === "help") {
    console.log(`[dsmonitor] 사용법: npx dsmonitor <command>\n  Commands:\n${COMMAND_LIST}`);
    return;
  }

  // v0.1.0: init subcommand — config 없어도 작동 (사용자 환경 dsmonitor/ 부트스트랩).
  if (cmd === "init") {
    const { runInit } = await import("./cli/init");
    await runInit();
    return;
  }

  if (!configPath) {
    console.error(
      `[dsmonitor] dsmonitor.config.ts 를 찾지 못했습니다.\n` +
        `  검색: cwd(${process.cwd()}) 부터 부모 디렉토리로 올라가며\n` +
        `    - <dir>/dsmonitor.config.ts\n` +
        `    - <dir>/dsmonitor/dsmonitor.config.ts\n` +
        `    - <dir>/vitaui.config.ts (legacy)\n` +
        `    - <dir>/vitaui/vitaui.config.ts (legacy)\n` +
        `  해결:\n` +
        `    1) npx dsmonitor init   # 부트스트랩 (인터랙티브)\n` +
        `    2) dsmonitor/ 디렉토리 안에서 실행하거나\n` +
        `    3) --config <path> 로 명시 지정`
    );
    process.exit(1);
  }

  // .env.local 로드 — configPath 확정 후 위치 결정.
  // 우선순위: --env <path>  >  VITAUI_ENV_FILE  >  <configDir>/.env.local
  // 파일 부재는 에러 아님 (figmaAnalysis=false 경로에선 env 불필요).
  const envCandidate =
    (envPath ? path.resolve(envPath) : undefined) ??
    process.env.VITAUI_ENV_FILE ??
    path.join(path.dirname(configPath), ".env.local");
  if (existsSync(envCandidate)) {
    // v0.1.0: ESM 호환 — dynamic import 흐름. dotenv 가 dependencies 안 보장됨.
    try {
      const dotenv = await import("dotenv");
      dotenv.config({ path: envCandidate });
    } catch {
      // dotenv 모듈 부재 (사용자 환경 미설치 환경) — figmaAnalysis=true 일 때만 analyzer 친절 오류.
    }
  }

  console.log(`[dsmonitor] config: ${configPath}`);
  const rawCfg = await loadConfig(configPath);
  const cfg = attachAbsRoot(configPath, rawCfg);
  console.log(`[dsmonitor] projectRoot: ${cfg.__absRoot}`);

  // 0.7.0 (BB): doctor 명령 — config / 환경변수 진단만 수행하고 종료.
  if (cmd === "doctor") {
    const { runDoctor, doctorExitCode } = await import("./cli/doctor");
    const report = runDoctor(cfg, configPath, {
      json: doctorJson,
      strict: doctorStrict,
    });
    process.exit(doctorExitCode(report, { strict: doctorStrict }));
  }

  if (cmd === "audit") {
    // v0.3.1 (2026-05-11): --only lighthouse — Lighthouse 단독 측정.
    // code analyzer + figma analyzer 호출 X. 옛 `node node_modules/dsmonitor/lighthouse/run.js` 단독 호출 흐름 일관 + 사용자 환경 직관 강화.
    if (only === "lighthouse") {
      const configDir = path.dirname(configPath);
      console.log(`[dsmonitor] --only lighthouse — Lighthouse 단독 측정`);
      await runLighthouse(configDir, cfg.lighthouse);
      return;
    }

    // --only figma: 기존 reports/ 의 최신 JSON 을 base 로 읽어 figma 섹션만 갱신.
    // 사용 시나리오 — code 측정 결과는 그대로 유지하고 figma 만 빠르게 재측정.
    if (only === "figma") {
      const configDir = path.dirname(configPath);
      const reportsDir = path.resolve(configDir, cfg.report.outputDir);
      const baseInput = findLatestReportJson(reportsDir, cfg.report.baselineFilenamePrefix);
      if (!baseInput) {
        console.error(
          `[dsmonitor] --only figma 는 기존 reports/ JSON 을 base 로 사용합니다. ` +
            `${reportsDir} 에 base 파일이 없습니다. ` +
            `먼저 'npx dsmonitor audit' 또는 'npx dsmonitor audit --only code' 로 base 생성하세요.`
        );
        process.exit(1);
      }
      if (!cfg.metrics.figmaAnalysis) {
        console.error(
          `[dsmonitor] cfg.metrics.figmaAnalysis=false. --only figma 사용 불가.`
        );
        process.exit(1);
      }
      console.log(`[dsmonitor] --only figma — base: ${baseInput}`);
      // 0.8.10 — JSON 파싱 방어 (utils/readJson — 손상 파일 시 친절 안내).
      const report = await readJsonFile<CodebaseReport>(baseInput, "baseline");
      const fT0 = Date.now();
      let instancesFile: FigmaInstancesFile | undefined;
      try {
        // --only figma 는 코드 측정을 다시 하지 않으므로 classIndex 미제공.
        // 컴포넌트 매칭 (B 그룹 단계 3) 미생성 — 통합 측정 (npx dsmonitor audit) 시점에만 산출.
        console.log(
          `[dsmonitor]   note: --only figma 는 componentMatch 미생성 ` +
            `(코드 인덱스 필요). 통합 측정 사용 권장.`
        );
        const result = await analyzeFigma(cfg);
        report.figma = result.report;
        instancesFile = result.instancesFile;
        const figmaElapsed = Date.now() - fT0;
        console.log(`[dsmonitor] figma analysis done in ${figmaElapsed}ms`);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error(`[dsmonitor] figma analysis failed: ${msg}`);
        process.exit(1);
      }
      const target = await writeReport(report, cfg, configDir, { baseline });
      console.log(`[dsmonitor] report: ${target}`);
      if (instancesFile) {
        await writeInstancesFile(instancesFile, cfg, configDir);
      }
      printSummary(report);
      return;
    }

    // 기본 흐름 (--only 미지정 또는 --only code)
    console.log(`[dsmonitor] analyzing codebase...`);
    const t0 = Date.now();
    // B 그룹 단계 3 (2026-04-29): analyzeCodebase 가 { report, classIndex } 반환.
    // classIndex 는 figma analyzer 의 컴포넌트 매칭 (B 그룹 단계 3) 분자 source.
    const { report, classIndex } = await analyzeCodebase(cfg);
    const codebaseElapsed = Date.now() - t0;
    console.log(`[dsmonitor] codebase analysis done in ${codebaseElapsed}ms`);

    // --only code 시 figma 강제 skip. 미지정 시 cfg.metrics.figmaAnalysis 그대로.
    const runFigma = only !== "code" && cfg.metrics.figmaAnalysis;
    let instancesFile: FigmaInstancesFile | undefined;
    if (runFigma) {
      console.log(`[dsmonitor] figma baseline enabled — analyzing...`);
      const fT0 = Date.now();
      try {
        const result = await analyzeFigma(cfg, classIndex);
        report.figma = result.report;
        instancesFile = result.instancesFile;
        const figmaElapsed = Date.now() - fT0;
        console.log(`[dsmonitor] figma analysis done in ${figmaElapsed}ms`);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error(`[dsmonitor] figma analysis failed: ${msg}`);
        console.error(
          `[dsmonitor] codebase 측정은 유지하고 figma 섹션 없이 리포트 생성합니다.`
        );
      }
    } else if (only === "code") {
      console.log(`[dsmonitor] --only code — figma 단계 건너뜀`);
    } else {
      console.log(`[dsmonitor] figma baseline disabled (metrics.figmaAnalysis=false)`);
    }

    const configDir = path.dirname(configPath);
    const target = await writeReport(report, cfg, configDir, { baseline });
    console.log(`[dsmonitor] report: ${target}`);
    if (instancesFile) {
      await writeInstancesFile(instancesFile, cfg, configDir);
    }
    printSummary(report);

    // v0.3.0 (2026-05-11): --all flag — 통합 측정 chain.
    // audit (code + figma) 완료 후 Lighthouse 호출 + report (markdown) + dashboard 자동 chain.
    // 사용자 환경 한 번 명령 호출로 모든 측정 + 출력 자동 흐름 진입.
    if (all) {
      console.log(``);
      console.log(`[dsmonitor] --all chain — Lighthouse + report + dashboard 진입`);

      // 1. Lighthouse 호출 (--skip-lighthouse X 시)
      if (!skipLighthouse) {
        await runLighthouse(configDir, cfg.lighthouse);
      } else {
        console.log(`[dsmonitor]   skip-lighthouse — Lighthouse 측정 건너뜀`);
      }

      // 2. markdown report 생성 (옛 report 명령 흐름 일관)
      console.log(`[dsmonitor] --all chain — markdown report 생성`);
      await runReportChain(cfg, configPath, target);

      // 3. dashboard 생성 (옛 dashboard 명령 흐름 일관)
      console.log(`[dsmonitor] --all chain — dashboard 빌드`);
      await runDashboardChain(cfg, configPath, target);

      console.log(``);
      console.log(`[dsmonitor] --all chain 완료.`);
    }
    return;
  }

  if (cmd === "baseline-lint") {
    console.log(`[dsmonitor] scanning for legacy utility class violations...`);
    const t0 = Date.now();
    const outPath = path.resolve(path.dirname(configPath), "lint-baseline.json");
    const baseline = await generateLintBaseline(cfg, outPath);
    const elapsed = Date.now() - t0;
    console.log(`[dsmonitor] done in ${elapsed}ms`);
    console.log(`[dsmonitor] baseline: ${outPath}`);
    console.log(`
=== Lint Baseline Summary ===
files scanned:           ${baseline.totals.filesScanned}
files with violations:   ${baseline.totals.filesWithViolations}
total violations:        ${baseline.totals.totalViolations}
by id:                   ${Object.entries(baseline.totals.byId).map(([k, v]) => `${k}=${v}`).join(", ") || "-"}
`);
    return;
  }

  if (cmd === "report") {
    const configDir = path.dirname(configPath);
    const reportsDir = path.resolve(configDir, cfg.report.outputDir);
    const resolvedInput = inputPath
      ? path.resolve(inputPath)
      : findLatestReportJson(reportsDir, cfg.report.baselineFilenamePrefix);
    if (!resolvedInput) {
      console.error(
        `[dsmonitor] no report JSON found in ${reportsDir}. Run 'npm run audit' or 'npm run audit:baseline' first.`
      );
      process.exit(2);
    }
    const resolvedOutput = outputPath
      ? path.resolve(outputPath)
      : path.resolve(path.dirname(configPath), "docs/baseline.md");
    console.log(`[dsmonitor] input:  ${resolvedInput}`);
    console.log(`[dsmonitor] output: ${resolvedOutput}`);
    const report = await readJsonFile<CodebaseReport>(resolvedInput, "baseline");
    await generateMarkdown(report, cfg, {
      inputPath: resolvedInput,
      outputPath: resolvedOutput,
    });
    console.log(`[dsmonitor] markdown report written.`);

    // Overview for stakeholders — 템플릿이 있으면 함께 생성
    const configDir2 = path.dirname(configPath);
    const templatePath = path.resolve(
      configDir2,
      "docs/overview-for-stakeholders.template.md"
    );
    const overviewOutPath = path.resolve(
      configDir2,
      "docs/overview-for-stakeholders.md"
    );
    const wroteOverview = await generateOverview(report, cfg, {
      templatePath,
      outputPath: overviewOutPath,
    });
    if (wroteOverview) {
      console.log(`[dsmonitor] overview written: ${overviewOutPath}`);
    } else {
      console.log(
        `[dsmonitor] overview template not found at ${templatePath} — skipped`
      );
    }
    return;
  }

  if (cmd === "dashboard") {
    const configDir = path.dirname(configPath);
    const reportsDir = path.resolve(configDir, cfg.report.outputDir);
    const resolvedInput = inputPath
      ? path.resolve(inputPath)
      : findLatestReportJson(reportsDir, cfg.report.baselineFilenamePrefix);
    if (!resolvedInput) {
      console.error(
        `[dsmonitor] no baseline JSON found in ${reportsDir}. ` +
          `Run 'npx dsmonitor audit' or 'npx dsmonitor audit --baseline' first.`
      );
      process.exit(2);
    }
    const stamp = todayStampUtc();
    const resolvedOutput = outputPath
      ? path.resolve(outputPath)
      : path.resolve(reportsDir, `dashboard-${stamp}.html`);
    console.log(`[dsmonitor] input:  ${resolvedInput}`);
    console.log(`[dsmonitor] output: ${resolvedOutput}`);
    await renderDashboard({
      inputPath: resolvedInput,
      outputPath: resolvedOutput,
      cfg,
      configDir,
    });
    console.log(`[dsmonitor] dashboard written.`);
    return;
  }

  if (cmd === "export-migration") {
    // Phase 0.7 (2026-04-29): figma-instances-{date}.json + frame 필터링 → CSV.
    const configDir = path.dirname(configPath);
    const reportsDir = path.resolve(configDir, cfg.report.outputDir);
    const frame = readArg(process.argv, "--frame");
    const ds = readArg(process.argv, "--ds") ?? "ds-legacy";
    if (!frame) {
      console.error(
        `[dsmonitor] export-migration: --frame=<frame-comment> 필수.\n` +
          `  사용 예: npx dsmonitor export-migration --frame=Test-Perform [--ds=ds-legacy]\n` +
          `  --ds 기본값: ds-legacy. 다른 값: ds-new / unmatched / all`
      );
      process.exit(2);
    }
    const instancesPath = inputPath
      ? path.resolve(inputPath)
      : findLatestInstancesJson(reportsDir);
    if (!instancesPath) {
      console.error(
        `[dsmonitor] export-migration: figma-instances-{date}.json 없음. ` +
          `먼저 'npx dsmonitor audit --baseline' 으로 측정 + 별도 파일 생성하세요.`
      );
      process.exit(2);
    }
    const stamp = todayStampUtc();
    const safeFrame = frame.replace(/[^a-zA-Z0-9_-]/g, "_");
    const safeDs = ds.replace(/[^a-zA-Z0-9_-]/g, "_");
    const resolvedOutput = outputPath
      ? path.resolve(outputPath)
      : path.resolve(
          configDir,
          "reports",
          "migration",
          `${safeFrame}-${safeDs}-${stamp}.csv`
        );
    console.log(`[dsmonitor] export-migration`);
    console.log(`[dsmonitor]   input:  ${instancesPath}`);
    console.log(`[dsmonitor]   output: ${resolvedOutput}`);
    console.log(`[dsmonitor]   frame:  ${frame}`);
    console.log(`[dsmonitor]   ds:     ${ds}`);
    const result = await exportMigrationCsv(instancesPath, {
      frame,
      ds,
      outputPath: resolvedOutput,
    });
    console.log(`[dsmonitor]   rows:   ${result.rowCount.toLocaleString()}`);
    console.log(`[dsmonitor] CSV written.`);
    return;
  }

  // 0.8.9 — 목록을 COMMAND_LIST 단일 원천으로 교체 (init / doctor 누락 정정).
  console.error(
    `[dsmonitor] Unknown command: ${cmd}.\n` +
      `  Supported:\n${COMMAND_LIST}`
  );
  process.exit(2);
}

/** 명령행 인자 헬퍼 — `--key=value` 또는 `--key value` 둘 다 지원. */
function readArg(argv: string[], key: string): string | undefined {
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i];
    if (a === key) return argv[i + 1];
    if (a.startsWith(`${key}=`)) return a.slice(key.length + 1);
  }
  return undefined;
}

/**
 * dsmonitor/reports/ 안에서 가장 최근 figma-instances-{date}.json 검색.
 * findLatestReportJson 과 같은 흐름 — prefix "figma-instances" 만 우선.
 */
function findLatestInstancesJson(reportsDir: string): string | null {
  // 0.8.10 — readdir + 정렬 패턴을 공유 유틸 (utils/latest) 로 교체 (옛 require 우회 제거).
  const name = findLatestName(reportsDir, (f) =>
    /^figma-instances-\d{4}-\d{2}-\d{2}\.json$/.test(f)
  );
  return name ? path.join(reportsDir, name) : null;
}

/**
 * v0.3.0 (2026-05-11) — --all chain 안 Lighthouse 호출.
 *
 * 0.5.0 (2026-05-14) BREAKING — 옛 `dsmonitor/lighthouse/config.js` 안
 *   PAGES hard-code 흐름 자체 폐기. `cfg.lighthouse.{baseUrl, pages, runs,
 *   auth, advanced}` 자체 read → `node_modules/.cache/dsmonitor/lighthouserc.js`
 *   자체 임시 파일 동적 생성 → `lhci autorun --config=<temp>` 자체 inject.
 *
 * 흐름:
 *   1. lighthouseScript (lighthouse/run.js) 자체 위치 자동 검색
 *   2. 어댑터 path 결정 (basic = 패키지 내장 / custom = config 안 상대 / none = 미사용)
 *   3. 임시 lighthouserc.js 자체 동적 생성 (default options + advanced deep-merge)
 *   4. run.js 자체 spawn — DSMONITOR_LIGHTHOUSE_CONFIG_PATH + AUTH_* env inject
 */
async function runLighthouse(
  configDir: string,
  lighthouseConfig?: LighthouseConfig
): Promise<void> {
  const { spawnSync } = await import("node:child_process");
  const here = path.dirname(url.fileURLToPath(import.meta.url));
  const candidates = [
    path.resolve(here, "../lighthouse/run.js"),
    path.resolve(here, "../../lighthouse/run.js"),
  ];
  let lighthouseScript: string | null = null;
  for (const c of candidates) {
    if (existsSync(c)) {
      lighthouseScript = c;
      break;
    }
  }
  if (!lighthouseScript) {
    console.error(
      `[dsmonitor]   lighthouse/run.js 위치 자동 검색 실패. 건너뜀.\n` +
        `  검색 위치:\n` +
        candidates.map((c) => `    - ${c}`).join("\n")
    );
    return;
  }

  // 어댑터 path 결정 (basic = 패키지 내장 / custom = config 안 상대 path / none = 미사용)
  const authType = lighthouseConfig?.auth?.type ?? "none";
  let adapterPath = "";
  if (authType === "basic") {
    const basicCandidates = [
      path.resolve(here, "../lighthouse/auth/basic-form-login.js"),
      path.resolve(here, "../../lighthouse/auth/basic-form-login.js"),
    ];
    for (const c of basicCandidates) {
      if (existsSync(c)) {
        adapterPath = c;
        break;
      }
    }
  } else if (lighthouseConfig?.auth?.type === "custom") {
    adapterPath = path.resolve(configDir, lighthouseConfig.auth.adapter);
  }

  // 임시 lighthouserc.js 자체 동적 생성 (0.5.0+)
  const tempConfigPath = writeLighthouseTempConfig(
    configDir,
    lighthouseConfig,
    adapterPath
  );
  const pagesCount = (lighthouseConfig?.pages?.length ?? 0) || 1;
  const runsCount = lighthouseConfig?.runs ?? 3;

  console.log(
    `[dsmonitor]   running Lighthouse (${pagesCount} URL × ${runsCount}회 = ${pagesCount * runsCount} runs) — script: ${lighthouseScript}`
  );
  console.log(`[dsmonitor]   auth: ${authType}${adapterPath ? ` (${adapterPath})` : ""}`);
  console.log(`[dsmonitor]   lighthouserc (temp): ${tempConfigPath}`);

  const res = spawnSync(process.execPath, [lighthouseScript], {
    cwd: process.cwd(),
    stdio: "inherit",
    env: {
      ...process.env,
      DSMONITOR_LIGHTHOUSE_AUTH_TYPE: authType,
      DSMONITOR_LIGHTHOUSE_AUTH_ADAPTER: adapterPath,
      DSMONITOR_LIGHTHOUSE_CONFIG_PATH: tempConfigPath,
      DSMONITOR_LIGHTHOUSE_PAGES_COUNT: String(pagesCount),
      DSMONITOR_LIGHTHOUSE_RUNS_COUNT: String(runsCount),
    },
  });
  if (res.status !== 0) {
    console.error(
      `[dsmonitor]   Lighthouse 측정 실패 (status ${res.status}). chain 계속 진행 (report + dashboard).`
    );
  } else {
    console.log(`[dsmonitor]   Lighthouse 측정 완료.`);
  }
}

/**
 * 0.5.0 — 임시 lighthouserc.js 자체 동적 생성. dsmonitor.config.ts 안
 * `lighthouse.{baseUrl, pages, runs, auth, advanced}` 자체 read → LHCI
 * config 자체 상세 build → `node_modules/.cache/dsmonitor/lighthouserc.js`
 * 자체 상세 write → path 자체 반환.
 *
 * 외부 사용자 자체 본 파일 자체 직접 정정 X — 매 측정 시점 상세 재생성.
 * dsmonitor 자체 default options 자체 + `lighthouse.advanced` 자체 deep-merge.
 */
function writeLighthouseTempConfig(
  _configDir: string,
  lighthouseConfig: LighthouseConfig | undefined,
  adapterPath: string
): string {
  const cwd = process.cwd();
  const cacheDir = path.join(cwd, "node_modules", ".cache", "dsmonitor");
  mkdirSync(cacheDir, { recursive: true });
  const tempPath = path.join(cacheDir, "lighthouserc.js");

  const baseUrlLiteral = lighthouseConfig?.baseUrl
    ? JSON.stringify(lighthouseConfig.baseUrl)
    : `process.env.LIGHTHOUSE_BASE_URL || "http://localhost:3000"`;
  const pages = lighthouseConfig?.pages?.length
    ? lighthouseConfig.pages.map((p) => p.path)
    : ["/"];
  const runs = lighthouseConfig?.runs ?? 3;
  const authType = lighthouseConfig?.auth?.type ?? "none";
  const useAuth = authType !== "none" && adapterPath !== "";

  const defaultSettings: Record<string, unknown> = {
    preset: "desktop",
    formFactor: "desktop",
    screenEmulation: {
      mobile: false,
      width: 1350,
      height: 940,
      deviceScaleFactor: 1,
      disabled: false,
    },
    onlyCategories: ["performance", "accessibility", "best-practices", "seo"],
    disableStorageReset: useAuth,
  };
  const advanced = (lighthouseConfig?.advanced ?? {}) as Record<string, unknown>;
  const advancedSettings = (advanced.settings ?? {}) as Record<string, unknown>;
  const settings = deepMerge(defaultSettings, advancedSettings);

  const puppeteerScriptLiteral = useAuth
    ? JSON.stringify(path.relative(cwd, adapterPath))
    : null;

  const body = `// dsmonitor 자체 동적 생성 — 0.5.0+ 임시 LHCI config.
// 본 파일 자체 = dsmonitor 자체 runLighthouse() 안 매 측정 시점 상세 재생성.
// 외부 사용자 자체 본 파일 자체 직접 정정 X — \`dsmonitor.config.ts\` 안 \`lighthouse\` 자체 정정.

const baseUrl = (${baseUrlLiteral}).replace(/\\/$/, "");
const PAGES = ${JSON.stringify(pages)};
const outputDir = process.env.LHCI_OUTPUT_DIR || "./reports";

const collect = {
  url: PAGES.map((p) => \`\${baseUrl}\${p}\`),
  numberOfRuns: ${runs},
  settings: ${JSON.stringify(settings, null, 2)},
};
${useAuth ? `collect.puppeteerScript = ${puppeteerScriptLiteral};\ncollect.puppeteerLaunchOptions = { headless: true };\n` : ``}
module.exports = {
  ci: {
    collect,
    upload: {
      target: "filesystem",
      outputDir,
      reportFilenamePattern: "%%PATHNAME%%-%%DATETIME%%-report.%%EXTENSION%%",
    },
  },
};
`;

  writeFileSync(tempPath, body);
  return tempPath;
}

/**
 * deep-merge — `lighthouse.advanced` 자체 안 deep-merge 흐름.
 * source 자체 안 nested object 자체 = recursive merge. 자체 = source 자체 우선.
 */
function deepMerge(
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...target };
  for (const [k, v] of Object.entries(source)) {
    const existing = result[k];
    if (
      v &&
      typeof v === "object" &&
      !Array.isArray(v) &&
      existing &&
      typeof existing === "object" &&
      !Array.isArray(existing)
    ) {
      result[k] = deepMerge(
        existing as Record<string, unknown>,
        v as Record<string, unknown>
      );
    } else {
      result[k] = v;
    }
  }
  return result;
}

/**
 * v0.3.0 (2026-05-11) — --all chain 측 markdown report 생성.
 *
 * 옛 report 명령 흐름 일관 — generateMarkdown + generateOverview chain.
 * baseline JSON path = audit 측 방금 작성한 target (writeReport 결과).
 */
async function runReportChain(
  cfg: UIHealthConfig & { __absRoot: string },
  configPath: string,
  baselineJsonPath: string
): Promise<void> {
  const configDir = path.dirname(configPath);
  const outputPath = path.resolve(configDir, "docs/baseline.md");
  console.log(`[dsmonitor]   input:  ${baselineJsonPath}`);
  console.log(`[dsmonitor]   output: ${outputPath}`);
  const report = await readJsonFile<CodebaseReport>(baselineJsonPath, "baseline");
  await generateMarkdown(report, cfg, {
    inputPath: baselineJsonPath,
    outputPath,
  });
  console.log(`[dsmonitor]   markdown report written.`);

  // overview-for-stakeholders 측 템플릿 존재 시 함께 생성
  const templatePath = path.resolve(
    configDir,
    "docs/overview-for-stakeholders.template.md"
  );
  const overviewOutPath = path.resolve(
    configDir,
    "docs/overview-for-stakeholders.md"
  );
  const wroteOverview = await generateOverview(report, cfg, {
    templatePath,
    outputPath: overviewOutPath,
  });
  if (wroteOverview) {
    console.log(`[dsmonitor]   overview written: ${overviewOutPath}`);
  }
}

/**
 * v0.3.0 (2026-05-11) — --all chain 측 dashboard 빌드.
 *
 * 옛 dashboard 명령 흐름 일관 — renderDashboard 호출.
 */
async function runDashboardChain(
  cfg: UIHealthConfig & { __absRoot: string },
  configPath: string,
  baselineJsonPath: string
): Promise<void> {
  const configDir = path.dirname(configPath);
  const reportsDir = path.resolve(configDir, cfg.report.outputDir);
  const stamp = todayStampUtc();
  const outputPath = path.resolve(reportsDir, `dashboard-${stamp}.html`);
  console.log(`[dsmonitor]   input:  ${baselineJsonPath}`);
  console.log(`[dsmonitor]   output: ${outputPath}`);
  await renderDashboard({
    inputPath: baselineJsonPath,
    outputPath,
    cfg,
    configDir,
  });
  console.log(`[dsmonitor]   dashboard written.`);
}

/**
 * Phase 0.7 별도 파일 출력 — dsmonitor/reports/figma-instances-{date}.json.
 * baseline JSON 과 분리, 시계열 보존 필요.
 */
async function writeInstancesFile(
  instancesFile: FigmaInstancesFile,
  cfg: UIHealthConfig,
  configDir: string
): Promise<void> {
  const reportsDir = path.resolve(configDir, cfg.report.outputDir);
  const stamp = (instancesFile.generatedAt || "").slice(0, 10);
  const outPath = path.join(reportsDir, `figma-instances-${stamp}.json`);
  await fs.mkdir(path.dirname(outPath), { recursive: true });
  await fs.writeFile(outPath, JSON.stringify(instancesFile, null, 2), "utf8");
  // 합산 stat — 사용자 인지.
  let totalInstances = 0;
  for (const d of instancesFile.domains) {
    for (const p of d.pages) {
      for (const f of p.frames ?? []) totalInstances += f.instances.length;
      if (p.instances) totalInstances += p.instances.length;
    }
  }
  console.log(
    `[dsmonitor] instances JSON: ${outPath} (${totalInstances.toLocaleString()} instance)`
  );
}

// 0.8.10 — any 제거 (필드 오타 시 컴파일 시점 검출).
function printSummary(r: CodebaseReport) {
  const d = r.stylingMethodDistribution;
  const allowedLine = Object.entries(d.allowed)
    .map(([k, v]) => `${k}=${v}`)
    .join(", ") || "-";
  const forbiddenLine = Object.entries(d.forbidden)
    .map(([k, v]) => `${k}=${v}`)
    .join(", ") || "-";
  const forbiddenOcc = Object.entries(r.forbiddenClassCount.byId)
    .map(([k, v]) => `${k}=${v}`)
    .join(", ") || "-";

  console.log(`
=== DSMonitor Baseline Summary ===
code files:            ${r.totals.codeFiles}
style files:           ${r.totals.styleFiles}
DS component files:    ${r.totals.dsComponentFiles}
non-DS component files:${r.totals.nonDsComponentFiles}

Hardcoded colors:      ${r.hardcodedColors.total}
Variable usages:       ${r.scssVariableCompliance.variableUsages}
Variable compliance:   ${(r.scssVariableCompliance.compliance * 100).toFixed(1)}%

Styling method distribution (files; non-exclusive):
  allowed:             ${allowedLine}
  forbidden:           ${forbiddenLine}
  allowedGlobal:       ${d.allowedGlobal}
  orphanClass:         ${d.orphanClass}
  noClass:             ${d.noClass}
  preferred (${d.preferredId}) compliance: ${(d.preferredCompliance.value * 100).toFixed(1)}%

Forbidden class occurrences:
  total:               ${r.forbiddenClassCount.total}
  by id:               ${forbiddenOcc}

TS Migration:          ${(r.tsMigration.ratio * 100).toFixed(1)}%  (TS ${r.tsMigration.tsFiles} / JS ${r.tsMigration.jsFiles})

DS Coverage:           ${(r.dsCoverage.coverage * 100).toFixed(1)}%  (${r.dsCoverage.filesUsingDs} / ${r.dsCoverage.totalConsumerFiles})

Migration candidates:
  total occurrences:   ${r.migrationCandidates.totalOccurrences}
  affected files:      ${r.migrationCandidates.totalFilesAffected}
  by target:           ${Object.entries(r.migrationCandidates.byTarget).map(([k, v]) => `${k}=${v}`).join(", ") || "-"}
`);
}

main().catch((e) => {
  // 0.8.10 — "[dsmonitor]" prefix 의 의도된 사용자 대면 에러는 message 만 출력
  //   (raw 스택 노출 X — readJson 방어 등). 그 외 예기치 못한 에러는 옛 흐름 그대로
  //   전체 출력 (디버깅 정보 보존).
  if (e instanceof Error && e.message.startsWith("[dsmonitor]")) {
    console.error(e.message);
  } else {
    console.error(e);
  }
  process.exit(1);
});
